import React, { useCallback, useRef, useState } from "react";
import { Upload, FileText, X } from "lucide-react";

export function UploadZone({ file, onFile, disabled, language }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const L = language === "fr"
    ? { cta: "Glissez-déposez votre CV ici", or: "ou", browse: "sélectionner un fichier", hint: "PDF ou DOCX · 10 Mo max", remove: "Retirer" }
    : { cta: "Drag & drop your CV here", or: "or", browse: "choose a file", hint: "PDF or DOCX · 10 MB max", remove: "Remove" };

  const handle = useCallback((f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!(name.endsWith(".pdf") || name.endsWith(".docx"))) {
      alert(language === "fr" ? "Format non supporté. PDF ou DOCX uniquement." : "Unsupported format. PDF or DOCX only.");
      return;
    }
    onFile(f);
  }, [onFile, language]);

  if (file) {
    return (
      <div className="flex items-center justify-between px-4 py-4 rounded-lg border border-[var(--border-bright)] bg-[var(--bg-2)]" data-testid="uploaded-file">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[var(--surface-2)] border border-[var(--border)] flex items-center justify-center">
            <FileText size={16} className="text-[var(--accent)]" />
          </div>
          <div>
            <div className="text-sm font-medium text-[var(--ink)]">{file.name}</div>
            <div className="text-xs text-[var(--ink-dim)] font-mono">{(file.size / 1024).toFixed(1)} KB</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onFile(null)}
          disabled={disabled}
          className="text-[var(--ink-dim)] hover:text-[var(--ink)] transition-colors"
          data-testid="remove-file-btn"
          aria-label={L.remove}
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`dropzone ${dragActive ? "active" : ""}`}
      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        handle(e.dataTransfer.files?.[0]);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      data-testid="upload-zone"
      role="button"
      tabIndex={0}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx"
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
        data-testid="file-input"
      />
      <div className="w-10 h-10 mx-auto rounded-full border border-[var(--border-bright)] bg-[var(--surface)] flex items-center justify-center mb-3">
        <Upload size={18} className="text-[var(--accent)]" strokeWidth={1.5} />
      </div>
      <div className="text-sm font-medium text-[var(--ink)]">{L.cta}</div>
      <div className="text-xs text-[var(--ink-dim)] mt-1">
        {L.or} <span className="underline underline-offset-2 text-[var(--ink)]">{L.browse}</span>
      </div>
      <div className="text-[11px] text-[var(--ink-dim)] mt-3 font-mono">{L.hint}</div>
    </div>
  );
}
